import { err, ok } from '@src/services/common/Result';

import { intersectSpan } from './clipSpanOverlap';
import { segmentStartMs } from './dubWordTimeline';

/**
 * The manifest shape is imported from the repository rather than moved or
 * restated here (PLAT-11511, decision 0005). An `import type` is erased at
 * compile time, so it carries no repository code into the editor and cannot
 * pull the network layer in behind it. It is also what the editor already does:
 * `DubDescriptionStore` holds one of these and reads the same declaration.
 *
 * That is a different rule from the one the engine follows when it restates the
 * on-screen control's audio mode. A component sits above the editor and the
 * editor must not name one at all; a repository does not sit above it, and the
 * manifest is the server's own description of the dub, which belongs with the
 * code that reads it off the server.
 */
import type {
  DubManifestDTO,
  DubManifestSegment,
} from '@src/repositories/DubbingRepository';
import type { Result } from '@src/services/common/Result';

/**
 * Turns a dub manifest into the audio elements that carry it inside one clip's
 * composition.
 *
 * Pure: no MobX, no React, no IO. It is handed a manifest, the signed rendition
 * URLs, and the clip's kept spans, and it returns element specs.
 *
 * - **Follow each segment's own `renditionId`.** A rendition boundary is a
 *   re-recording pass, NOT a change of speaker or voice, so nothing may be
 *   derived from how many rendition files exist.
 * - **Always emit an explicit `trim_start` AND `duration`.** A rendition file can
 *   hold stale re-recorded audio the manifest references nowhere, so an
 *   untrimmed element plays that stale sound and still renders successfully. And
 *   an audio element with no `duration` is measured by DOWNLOADING it at publish
 *   time, once per element.
 * - **Merge runs.** Consecutive segments that share a rendition, sit contiguously
 *   inside it, and are placed with no gap are one unbroken piece of sound.
 *   Merging them moves no audio by a millisecond and leaves far fewer joins to
 *   go wrong.
 * - **Never attach volume keypoints to a dub element.** The render route explodes
 *   any audio element carrying them across two tracks, one invented on the spot.
 *   This service never emits them; callers must not add them.
 * - **A superseded segment must not be placed.** Regenerating a dub currently
 *   unions old and new lines in the stored manifest instead of replacing them,
 *   so a stale duplicate would be placed as its own element beside the live
 *   one — two voices in the delivered file, with the render reporting success.
 *
 * The caller supplies the clip's spans. Each span carries where it starts in the
 * source video AND where it starts on the clip's own timeline; the two are
 * unrelated numbers and this service never assumes they are the same. The clip
 * clock does NOT restart on each span — a span's `clipStartSec` already carries
 * its position on it, which is why no running offset is added anywhere below.
 *
 * - **A line is placed wherever the clip shows the picture it belongs to, and
 *   nowhere else.** A clip stitched from several kept spans — an ordinary
 *   montage, and every reel — cuts through lines. Each line is compared against
 *   EVERY span and produces one piece per span it overlaps, trimmed to that
 *   span's edges, with its own start on the clip clock and its own offset into
 *   the rendition. Placing it into only one span leaves the rest of the clip
 *   playing with the original silenced and nothing over it.
 * - **The merge compares POSITIONS, not which span a piece came from.** Where two
 *   spans meet in the source there is no cut in the picture, so a line split at
 *   that seam is put back together; where the clip removed picture, the later
 *   piece skips forward inside the file by the audio belonging to the removed
 *   picture, so the positions do not meet and the pieces stay separate. No test
 *   for "is there a cut here" is needed anywhere.
 */

const logPrefix = 'DubPlacementService';

/** One kept stretch of the source video, as it sits on the clip's own timeline. */
export interface ClipSpan {
  /** Where the span begins in the FULL SOURCE video, in seconds. */
  sourceStartSec: number;
  /** How long the span runs, in seconds. */
  durationSec: number;
  /** Where the span begins on the CLIP's own timeline, in seconds. */
  clipStartSec: number;
}

/** The properties of one dub audio element, ready to hand to the editor. */
export interface DubAudioElementSpec {
  type: 'audio';
  /** Signed URL of the rendition this element plays. */
  source: string;
  /** Position on the clip's timeline, in seconds. */
  time: number;
  /** Length on the timeline, in seconds. Always emitted — never left implicit. */
  duration: number;
  /** Offset into the rendition file, in seconds. Always emitted. */
  trim_start: number;
  /** The manifest segments this element carries, in order. */
  segmentIds: string[];
  /** The rendition this element plays, carried for diagnostics. */
  renditionId: string;
}

export interface DubPlacementPlan {
  elements: DubAudioElementSpec[];
  /** Segments dropped, with the reason — never silent. */
  skipped: { segmentId: string; reason: string }[];
  /** How many manifest segments ended up carried by the elements. */
  placedSegmentCount: number;
}

/**
 * A placement that puts nothing on the clip. Composing from it is exactly a
 * clean-up: the previous run's elements are removed, the original recording goes
 * back to full level, and the record entries are dropped — which is what a clip
 * whose story dub was deleted needs.
 */
export function emptyDubPlacement(): DubPlacementPlan {
  return { elements: [], skipped: [], placedSegmentCount: 0 };
}

export interface DubPlacementInput {
  manifest: DubManifestDTO;
  /** `renditionId` → signed URL, as returned beside the manifest. */
  renditionUrls: Record<string, string>;
  /** The clip's kept spans, in timeline order. */
  spans: ClipSpan[];
  /**
   * Lines played at their own level (`segmentLevels.ts`). Each is kept a piece
   * of its own — never merged with a neighbouring line — so its element can
   * carry its level. Pieces of the same line still join.
   */
  isolatedSegmentIds?: ReadonlySet<string>;
}

/**
 * Drop superseded duplicates. Two entries sharing a `segmentId` are the same line
 * dubbed twice; the newer one wins. Ordering is by `producedAt` when both carry
 * it, otherwise the later entry in the manifest is treated as the newer.
 */
export function dropSuperseded(segments: DubManifestSegment[]): {
  live: DubManifestSegment[];
  skipped: { segmentId: string; reason: string }[];
} {
  const bestBySegmentId = new Map<string, DubManifestSegment>();
  const skipped: { segmentId: string; reason: string }[] = [];

  for (const segment of segments) {
    const current = bestBySegmentId.get(segment.segmentId);
    if (!current) {
      bestBySegmentId.set(segment.segmentId, segment);
      continue;
    }
    const currentAt = current.producedAt ?? '';
    const candidateAt = segment.producedAt ?? '';
    const candidateIsNewer = candidateAt >= currentAt;
    const loser = candidateIsNewer ? current : segment;
    bestBySegmentId.set(
      segment.segmentId,
      candidateIsNewer ? segment : current,
    );
    skipped.push({
      segmentId: loser.segmentId,
      reason:
        'superseded by a later recording of the same line (PLAT-10632 leaves both in the manifest)',
    });
  }

  return { live: [...bestBySegmentId.values()], skipped };
}

/**
 * The lines of a dub that can be placed at all, in story-timeline order: a
 * superseded copy of a line is dropped, and so is a line whose recording has no
 * signed address. Both skips are reported, never silent.
 *
 * Shared by the audio (`planDubPlacement`) and the dub-timed captions
 * (`buildDubWordTimeline`), so a caption is shown for exactly the lines the
 * audio places — a line the voice never plays must not be read on screen.
 */
export function placeableSegments(
  manifest: DubManifestDTO,
  renditionUrls: Record<string, string>,
): {
  live: DubManifestSegment[];
  skipped: { segmentId: string; reason: string }[];
} {
  const { live, skipped } = dropSuperseded(manifest.segments);
  const placeable: DubManifestSegment[] = [];
  for (const segment of [...live].sort(
    (a, b) => segmentStartMs(a) - segmentStartMs(b),
  )) {
    // Skip the line loudly rather than emitting a sourceless element.
    if (!renditionUrls[segment.renditionId]) {
      skipped.push({
        segmentId: segment.segmentId,
        reason: `no signed URL for rendition ${segment.renditionId}`,
      });
      continue;
    }
    placeable.push(segment);
  }
  return { live: placeable, skipped };
}

/**
 * Where a moment of the recording falls on the clip's own clock, inside one
 * kept span. The clip clock does not restart per span: the span's
 * `clipStartSec` already carries its position.
 */
export function recordingSecToClipSec(
  recordingSec: number,
  span: ClipSpan,
): number {
  return span.clipStartSec + (recordingSec - span.sourceStartSec);
}

/**
 * The part of a stretch of the recording that one kept span shows, placed on
 * the clip's clock: where it starts there, how long it runs, and how much of
 * the stretch's head the span cut off. `null` when the span shows none of it.
 *
 * The dub audio's pieces are placed through it. The dub captions' words share
 * its point mapping (`recordingSecToClipSec`), so a word is shown exactly where
 * its sound plays; each word picks its stretch by its middle instead of by
 * overlap, so a very short word is never dropped as a sliver.
 */
export function mapRecordingSpanToClip(
  startSec: number,
  endSec: number,
  span: ClipSpan,
): { clipStartSec: number; durationSec: number; headTrimSec: number } | null {
  const overlap = intersectSpan(startSec, endSec, span);
  if (!overlap) return null;
  return {
    clipStartSec: recordingSecToClipSec(overlap.startSec, span),
    durationSec: overlap.durationSec,
    headTrimSec: overlap.startSec - startSec,
  };
}

/**
 * Do two pieces butt together on the clip's clock — where the first ends is
 * where the second begins? Compared in milliseconds so floating point never
 * decides it. The clip-clock half of the audio's continuity test, shared with
 * the dub captions, which break a row only where this is false.
 */
export function piecesTouchOnClip(
  a: { timeSec: number; durationSec: number },
  b: { timeSec: number },
): boolean {
  const aEndMs = Math.round((a.timeSec + a.durationSec) * 1000);
  const bStartMs = Math.round(b.timeSec * 1000);
  return aEndMs === bStartMs;
}

/** One part of one line, placed into one kept span of the clip. */
interface PlacedPiece {
  segment: DubManifestSegment;
  /** Clip-timeline start, seconds. */
  timeSec: number;
  /** Length on the timeline after any boundary trim, seconds. */
  durationSec: number;
  /** Offset into the rendition after any head trim, seconds. */
  trimStartSec: number;
}

/**
 * Place one segment against EVERY span of the clip it overlaps, trimming each
 * piece to that span's edges. Returns an empty array when the segment lies
 * entirely outside the clip — the only case that counts as dropping the line.
 */
function placeSegmentPieces(
  segment: DubManifestSegment,
  spans: ClipSpan[],
): PlacedPiece[] {
  const startSec = segmentStartMs(segment) / 1000;
  const endSec = startSec + segment.renditionDurationMs / 1000;
  const fileOffsetSec = segment.renditionOffsetMs / 1000;

  const pieces: PlacedPiece[] = [];
  for (const span of spans) {
    const mapped = mapRecordingSpanToClip(startSec, endSec, span);
    if (!mapped) continue;

    pieces.push({
      segment,
      timeSec: mapped.clipStartSec,
      durationSec: mapped.durationSec,
      // Whatever was cut off this piece's head must also be skipped inside the
      // file, so a piece in a later span plays the right words rather than
      // repeating the first ones.
      trimStartSec: fileOffsetSec + mapped.headTrimSec,
    });
  }
  return pieces;
}

/** Do two placed pieces form one unbroken piece of sound? */
function areContinuous(
  a: PlacedPiece,
  b: PlacedPiece,
  isolated: ReadonlySet<string> | undefined,
): boolean {
  if (a.segment.renditionId !== b.segment.renditionId) return false;
  if (
    a.segment.segmentId !== b.segment.segmentId &&
    (isolated?.has(a.segment.segmentId) || isolated?.has(b.segment.segmentId))
  ) {
    return false;
  }
  // Contiguous inside the rendition file: where `a` ends is where `b` begins.
  // Measured on the PIECES rather than on the raw manifest numbers, because a
  // line the clip cut through no longer plays all of itself — and because two
  // pieces of the SAME line must be able to join.
  const aFileEndMs = Math.round((a.trimStartSec + a.durationSec) * 1000);
  const bFileStartMs = Math.round(b.trimStartSec * 1000);
  if (aFileEndMs !== bFileStartMs) return false;
  // …and butted together on the timeline.
  return piecesTouchOnClip(a, b);
}

/**
 * Build the audio elements that carry a dub inside one clip.
 *
 * Returns a VALIDATION error only when the input cannot be used at all; a
 * manifest whose segments all fall outside the clip is a valid empty plan, not
 * an error.
 */
export function planDubPlacement(
  input: DubPlacementInput,
): Result<DubPlacementPlan> {
  const { manifest, renditionUrls, spans, isolatedSegmentIds } = input;

  if (!manifest || !Array.isArray(manifest.segments)) {
    return err('VALIDATION', `${logPrefix}: manifest has no segments array`);
  }
  if (!Array.isArray(spans) || spans.length === 0) {
    return err('VALIDATION', `${logPrefix}: the clip has no spans`);
  }

  const { live, skipped } = placeableSegments(manifest, renditionUrls);

  const placed: PlacedPiece[] = [];
  for (const segment of live) {
    // Only a line the clip carries NONE of is reported. A line that lost part of
    // itself to removed picture is not: that cut is correct, and reporting it
    // would read as if the line were missing when it is not.
    const pieces = placeSegmentPieces(segment, spans);
    if (pieces.length === 0) {
      skipped.push({
        segmentId: segment.segmentId,
        reason: 'falls outside every kept span of this clip',
      });
      continue;
    }
    placed.push(...pieces);
  }

  // The merge below only ever compares a piece with the one before it, so the
  // pieces have to be in clip order first — and they are not: a reel's sections
  // can run out of source order. Out of order, two pieces that genuinely run on
  // from each other are separated by an unrelated piece and never join. The sort
  // is stable, so two pieces starting at the same moment keep the order the
  // manifest gave them.
  placed.sort((a, b) => a.timeSec - b.timeSec);

  const elements: DubAudioElementSpec[] = [];
  let run: PlacedPiece[] = [];
  const flush = () => {
    if (run.length === 0) return;
    const first = run[0];
    const last = run[run.length - 1];
    elements.push({
      type: 'audio',
      source: renditionUrls[first.segment.renditionId],
      time: first.timeSec,
      duration: last.timeSec + last.durationSec - first.timeSec,
      trim_start: first.trimStartSec,
      segmentIds: run.map(p => p.segment.segmentId),
      renditionId: first.segment.renditionId,
    });
    run = [];
  };

  for (const p of placed) {
    if (
      run.length > 0 &&
      !areContinuous(run[run.length - 1], p, isolatedSegmentIds)
    )
      flush();
    run.push(p);
  }
  flush();

  return ok({
    elements,
    skipped,
    // Distinct LINES carried, not pieces — a line the clip cut through produces
    // several pieces and must still count once.
    placedSegmentCount: new Set(placed.map(p => p.segment.segmentId)).size,
  });
}
